import type { JSXChild } from 'engine/index';
import { ChevronRight, User } from 'lucide-static';
import { Icon } from './Icon';
import { unreachable } from 'shared/util';
import { pieceClasses } from './PieceSelector';

export function AppShell(
	{ gameName, bottomUI, onBind }: { gameName?: string; bottomUI?: JSXChild; onBind?: (view: HTMLElement) => void },
	children: JSXChild[],
) {
	const view: HTMLElement = (
		<div>
			<div class="fixed left-0 top-0 z-10 flex w-full shrink-0 items-center gap-3 bg-theme-600 px-3 py-1 text-theme-50 shadow-sm shadow-theme-950/30">
				<img src="/logo-white.svg" class="size-12" alt="bedrijfshelden logo" />
				<div class="flex items-center gap-1">
					<Icon icon={ChevronRight} class="size-3 shrink-0 text-theme-200" />
					<span class="text-xs md:text-base">{gameName ?? __`Game name`}</span>
				</div>
				<div class="flex-1 shrink-0"></div>
				<div class="flex items-center gap-2">
					<div class="flex items-center gap-2 p-1 text-center">
						<div user-icon class="rounded-full bg-theme-500 p-1 text-theme-200">
							<Icon icon={User} class="size-4 shrink-0" />
						</div>
						<span user-name class="text-xs text-theme-100 md:text-sm">
							{__`No name`}
						</span>
					</div>
					{/* <button class="flex size-9 items-center justify-center rounded-md bg-theme-700/30">
						<Icon icon={LogOut} class="size-4 shrink-0 text-theme-200" />
					</button> */}
				</div>
			</div>
			<div class="pointer-events-none fixed bottom-4 left-4 z-10 flex gap-2">{bottomUI}</div>
			<div shell-content class="flex min-h-screen justify-center pb-20 pt-14">
				{children}
			</div>
		</div>
	);

	if (onBind) {
		onBind(view);
	}

	return view;
}

export function StatBubble({
	color,
	label,
	id,
	value,
	onClick,
}: {
	color: 'yellow' | 'red' | 'green' | 'purple';
	label: string;
	id: string;
	value: string;
	onClick?: () => void;
}) {
	let colorClasses = '';
	// prettier-ignore
	switch(color) {
		case 'yellow': colorClasses = 'bg-yellow-400 text-yellow-950 shadow-orange-700/30'; break;
		case 'red': colorClasses = 'bg-red-600 text-red-50 shadow-red-700/30'; break;
		case 'green': colorClasses = 'bg-green-600 text-green-50 shadow-green-700/30'; break;
		case 'purple': colorClasses = 'bg-purple-600 text-purple-50 shadow-purple-700/30'; break;
		default: unreachable(color)
	}

	return (
		<div
			on:click={() => {
				// console.log('clicked...');
				if (onClick) onClick();
			}}
			class={'flex size-16 flex-col justify-center rounded-full text-center leading-none shadow-sm ' + colorClasses}
			style="pointer-events: all;"
		>
			<span id={id} class="text-lg">
				{value}
			</span>
			<small class="text-[9px]">{label}</small>
		</div>
	);
}

export function setUserIconColor(el: HTMLElement, color: string) {
	const entry = pieceClasses.find((e) => e.color === color);

	let classes = '';
	if (color === '') {
		classes = 'bg-theme-600 text-theme-200 border-theme-400';
	} else if (entry) {
		classes = entry.classes;
	} else if (!isNaN(Number(color))) {
		classes = 'bg-theme-600 text-theme-200 border-theme-400';
	} else {
		throw new Error(`Unhandled color ${color}`);
	}

	el.setAttribute('class', classes + ' p-1 rounded-full border');
}

// NOTE: taken from modal.tsx on console-client
export function showModal(
	modal: HTMLElement | ((confirm: () => void, cancel: () => void) => HTMLElement),
	settings: { passiveDismiss: boolean } = { passiveDismiss: false },
): Promise<boolean> {
	return new Promise<boolean>((resolve, reject) => {
		const confirm = () => {
			resolve(true);
			remove();
			return;
		};

		const cancel = () => {
			resolve(false);
			remove();
			return;
		};

		const modal2: HTMLElement = typeof modal === 'function' ? modal(confirm, cancel) : modal;

		const view: HTMLElement = (
			<div
				on:click={(e: MouseEvent) => {
					if (settings.passiveDismiss && e.target === view) {
						remove();
					}
				}}
				class="fixed inset-0 z-[99] flex max-h-screen items-center justify-center overflow-hidden bg-black/30 p-8"
			>
				{modal2}
			</div>
		);
		let removing = false;

		const handleClick = (e: MouseEvent) => {
			if (removing) return;

			if (e.target && e.target instanceof HTMLElement) {
				if (e.target.hasAttribute('action-cancel') || e.target.closest('[action-cancel]')) {
					cancel();
					return;
				}

				if (e.target.hasAttribute('action-confirm') || e.target.closest('[action-confirm]')) {
					confirm();
					return;
				}
			}
		};

		const remove = () => {
			removing = true;
			view.animate([{ backgroundColor: view.style.backgroundColor }, { backgroundColor: 'transparent' }], {
				duration: 200,
				easing: 'ease-out',
				fill: 'forwards',
			});

			modal2
				.animate(
					[
						{ transform: 'translateY(0rem)', opacity: '1' },
						{ transform: 'translateY(8rem)', opacity: '0' },
					],
					{
						duration: 300,
						easing: 'cubic-bezier(0, 0.56, 0, 0.99)',
						fill: 'forwards',
					},
				)
				.finished.then(() => view.remove());
		};

		view.addEventListener('click', handleClick);

		if (!modal2.style.willChange) {
			modal2.style.willChange = 'transform';
		}

		const app = document.querySelector('#app') as HTMLElement;
		app.append(view);

		view.animate([{ backgroundColor: 'transparent' }, { backgroundColor: view.style.backgroundColor }], {
			duration: 300,
			easing: 'ease-out',
		});

		modal2.animate(
			[
				{ transform: 'translateY(8rem)', opacity: '0.4' },
				{ transform: 'translateY(0rem)', opacity: '1' },
			],
			{
				duration: 400,
				easing: 'cubic-bezier(0, 0.56, 0, 0.99)',
			},
		);
	});
}
